import { AbstractComponentsPlatformService } from './AbstractComponentsPlatformService';
import type {
  ComponentInitialSize,
  DisplayFilters,
  EditorElement,
  ElementItem,
  InlineElement,
} from '@wix/component-protocol/schema';
import { LAYOUT } from '@wix/component-protocol/schema';
import { DataType } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import type { EditorReactComponent } from '@wix/ambassador-devcenter-v1-component-type-data/types';

import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';
import { CacheMethodResponse, MethodCacheStrategy } from '../api';
import { DependencyToken } from '../dependencies';
import { ManifestComponentExtensionType } from '../stores';
import { ValueOf, TpaStyle } from '@wix/document-services-types';
import { applyDisplayFilter } from '../utils/applyDisplayFilter';
import type { AbstractVirtualManifestBuilder } from '../builders';
import type { ManifestResolver } from '../api/ManifestResolver/ManifestResolver';
import { APPLIED_PRESET_FIELD } from '../presets/constants';

export class ManifestPlatformService extends AbstractComponentsPlatformService {
  static isBoxLike(
    layout: NonNullable<EditorElement['layout']>,
    data: NonNullable<EditorElement['data']>,
  ) {
    if (!layout.contentFill?.dataItemKey) {
      return false;
    }

    /**
     * TODO: check logic, seems broken after latest commits
     * at least the forEach does not make sense
     */
    const contentFillKey = layout.contentFill.dataItemKey;
    const currentData: any = data;
    let containerData: NonNullable<EditorElement['data']>[string] | undefined;

    contentFillKey.split('.').forEach((key: string) => {
      if (!currentData) {
        return;
      }
      const dataItem = currentData[key];
      if (!dataItem) {
        return;
      }
      if (dataItem.dataType === DataType.container) {
        containerData = dataItem;
      }
    });

    return Boolean(
      containerData &&
        containerData.dataType === 'container' &&
        containerData.container?.containerType === 'simple' &&
        !containerData.container.simple?.behaviors?.selectable,
    );
  }

  @CacheMethodResponse({
    cacheStrategy: MethodCacheStrategy.PresetOrStateUpdate,
  })
  isBoxLike(
    type: string,
    context: {
      // state?: string | undefined;
      preset?: string | undefined;
    } = {},
  ) {
    const manifest = this.manifestStore.getManifest(type);

    if (!manifest) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.PlatformComponentsService,
        'manifest doest not exist',
      );
    }

    const api = this.virtualElementProvider
      .buildVirtualElement({
        contextBuilder: (builder) => {
          return builder
            .withoutComponents()
            .contributeExternalAPI(DependencyToken.BUILDER_PRESETS_GET, () => {
              return () => {
                return {
                  presetName: context?.preset,
                };
              };
            })
            .withEditorElement(manifest.editorElement ?? {});
        },
      })
      .getAPI();

    return ManifestPlatformService.isBoxLike(
      api.resolver.getLayoutDefinitions(),
      api.resolver.getDataDefinitions(),
    );
  }

  getInstallationByComponentType(
    componentType: string,
  ): EditorReactComponent['installation'] | null {
    const manifest = this.manifestStore.getManifest(componentType);
    return manifest?.installation ?? null;
  }

  getFixedPositionByComponentType(
    componentType: string,
  ): NonNullable<EditorReactComponent['installation']>['fixedPosition'] | null {
    const installation = this.getInstallationByComponentType(componentType);
    return installation?.fixedPosition ?? null;
  }

  private mapBreakpointToBreakpointEnum = (
    breakpoint: keyof NonNullable<
      NonNullable<EditorReactComponent['installation']>['presets']
    >,
  ): ValueOf<typeof LAYOUT.BREAKPOINT> => {
    switch (breakpoint) {
      case 'default':
      case 'large':
        return LAYOUT.BREAKPOINT.large;
      case 'small':
        return LAYOUT.BREAKPOINT.small;
      default:
        return LAYOUT.BREAKPOINT.UNKNOWN_Breakpoint;
    }
  };

  private getPresetDefinitionsByComponentType(
    componentType: string,
    options?: {
      breakpoint?: keyof NonNullable<
        NonNullable<EditorReactComponent['installation']>['presets']
      >;
    },
    shouldApplyDisplayFilters = true,
  ): EditorElement['presets'] | null {
    const manifest = this.manifestStore.getManifest(componentType);
    const presets = manifest?.editorElement?.presets;

    if (!presets) {
      return null;
    }

    const presetsToResolve = shouldApplyDisplayFilters
      ? applyDisplayFilter(
          presets,
          manifest.editorElement?.displayFilters?.presets,
        )
      : presets;

    const { breakpoint } = options ?? {};

    if (!breakpoint) {
      return presetsToResolve;
    }

    const breakpointEnum = this.mapBreakpointToBreakpointEnum(breakpoint);
    return Object.fromEntries(
      Object.entries(presetsToResolve).filter(
        ([_, preset]) =>
          !preset.optimizedFor ||
          preset.optimizedFor.length === 0 ||
          preset.optimizedFor.includes(breakpointEnum),
      ),
    );
  }

  getPresetsByComponentType(
    componentType: string,
    options?: {
      breakpoint?: keyof NonNullable<
        NonNullable<EditorReactComponent['installation']>['presets']
      >;
    },
  ): EditorElement['presets'] | null {
    return this.getPresetDefinitionsByComponentType(componentType, options);
  }

  private applyDisplayFiltersToElement(
    editorElement: EditorElement,
    resolver: Pick<ManifestResolver, 'getDisplayFilters'>,
  ): EditorElement {
    const filtered = { ...editorElement };

    const filterableKeys: Exclude<keyof DisplayFilters, 'elements'>[] = [
      'data',
      'actions',
      'cssProperties',
      'cssCustomProperties',
      'customActions',
      'states',
      'presets',
    ];

    for (const key of filterableKeys) {
      if (filtered[key]) {
        // TS can't narrow `filtered[key]` when `key` is a union — safe because
        // applyDisplayFilter preserves the record shape and filterableKeys are all Record<string, *>
        (filtered as Record<string, unknown>)[key] = applyDisplayFilter(
          filtered[key] as Record<string, unknown>,
          resolver.getDisplayFilters(key),
        );
      }
    }

    if (filtered.elements) {
      const elementsFilter = resolver.getDisplayFilters('elements');
      if (elementsFilter) {
        filtered.elements = applyDisplayFilter(
          filtered.elements,
          elementsFilter,
        );
      }

      filtered.elements = Object.fromEntries(
        Object.entries(filtered.elements).map(([key, child]) => {
          const inlineElement = (child as ElementItem)?.inlineElement as
            | EditorElement
            | undefined;

          if (!inlineElement) {
            return [key, child];
          }

          const childApi = this.virtualElementProvider
            .buildVirtualElement({
              contextBuilder: (builder: AbstractVirtualManifestBuilder) => {
                return builder
                  .withoutComponents()
                  .withEditorElement(inlineElement as InlineElement);
              },
            })
            .getAPI();

          return [
            key,
            {
              ...child,
              inlineElement: this.applyDisplayFiltersToElement(
                inlineElement,
                childApi.resolver,
              ),
            },
          ];
        }),
      ) as typeof filtered.elements;
    }

    return filtered;
  }

  getManifestByComponentType(
    componentType: string,
    options?: { preset?: string; state?: string },
  ): EditorElement | null {
    const manifest = this.manifestStore.getManifest(componentType);

    if (!manifest?.editorElement) {
      return null;
    }

    const editorElement = manifest.editorElement;
    const extensionType = this.manifestStore.getExtensionType(componentType);

    const presetStyleMock = {
      style: { strings: { [APPLIED_PRESET_FIELD]: options?.preset ?? '' } },
    } as unknown as TpaStyle;

    const api = this.virtualElementProvider
      .buildVirtualElement({
        contextBuilder: (builder: AbstractVirtualManifestBuilder) => {
          return builder
            .withoutComponents()
            .contributeExternalAPI(DependencyToken.BUILDER_PRESETS_GET, () => {
              return () => ({
                presetName: options?.preset,
              });
            })
            .contributeExternalAPI(
              DependencyToken.BUILDER_PRESETS_GET_EFFECTIVE,
              () => {
                return () => ({
                  presetName: options?.preset,
                });
              },
            )
            .contributeExternalAPI(DependencyToken.OOI_STYLE_GET, () => {
              return () => presetStyleMock;
            })
            .contributeExternalAPI(
              DependencyToken.OOI_STYLE_GET_EFFECTIVE,
              () => {
                return () => presetStyleMock;
              },
            )
            .contributeExternalAPI(DependencyToken.VIEW_STATE_GET, () => {
              return () => options?.state ?? null;
            })
            .withEditorElement(editorElement, {
              componentType,
              extensionType:
                extensionType ?? ManifestComponentExtensionType.UNKNOWN,
            });
        },
      })
      .getAPI();

    if (options?.state && editorElement.states?.[options.state]) {
      api.query.setState(options.state);
    }

    return this.applyDisplayFiltersToElement(editorElement, api.resolver);
  }

  getDisplayNameByComponentType(componentType: string): string | null {
    const manifest = this.manifestStore.getManifest(componentType);
    return manifest?.editorElement?.displayName ?? null;
  }

  getDesignStateDefinitionsByComponentType(
    componentType: string,
  ): NonNullable<EditorElement['states']> {
    const manifest = this.manifestStore.getManifest(componentType);
    const editorElement = manifest?.editorElement;

    if (!editorElement) {
      return {};
    }

    const extensionType = this.manifestStore.getExtensionType(componentType);

    // No component instance exists for a type-only lookup, so stub every
    // getter that resolving the state display filters would otherwise route to
    // the live document (applied preset, view state, design states). Without
    // these stubs the resolver reaches into the host for a component that isn't
    // on the stage and throws "Component not found" (see
    // ExternalPresetsHandler.getAppliedPreset / ExternalStateHandler). With no
    // instance nothing is applied, which is the correct base definition set.
    const presetStyleMock = {
      style: { strings: { [APPLIED_PRESET_FIELD]: '' } },
    } as unknown as TpaStyle;

    return this.virtualElementProvider
      .buildVirtualElement({
        contextBuilder: (builder: AbstractVirtualManifestBuilder) => {
          return builder
            .withoutComponents()
            .contributeExternalAPI(DependencyToken.BUILDER_PRESETS_GET, () => {
              return () => ({ presetName: undefined });
            })
            .contributeExternalAPI(
              DependencyToken.BUILDER_PRESETS_GET_EFFECTIVE,
              () => {
                return () => ({ presetName: undefined });
              },
            )
            .contributeExternalAPI(DependencyToken.OOI_STYLE_GET, () => {
              return () => presetStyleMock;
            })
            .contributeExternalAPI(
              DependencyToken.OOI_STYLE_GET_EFFECTIVE,
              () => {
                return () => presetStyleMock;
              },
            )
            .contributeExternalAPI(DependencyToken.VIEW_STATE_GET, () => {
              return () => null;
            })
            .contributeExternalAPI(DependencyToken.DESIGN_STATES_GET, () => {
              return () => [];
            })
            .withEditorElement(editorElement, {
              componentType,
              extensionType:
                extensionType ?? ManifestComponentExtensionType.UNKNOWN,
            });
        },
      })
      .getAPI()
      .resolver.getDesignStateDefinitions();
  }

  getInitialSizeByComponentType(
    componentType: string,
    options?: {
      breakpoint?: keyof NonNullable<
        NonNullable<EditorReactComponent['installation']>['presets']
      >;
      preset?: string;
    },
  ): ComponentInitialSize | null {
    const { breakpoint, preset } = options ?? {};
    const presetDefinitions = this.getPresetDefinitionsByComponentType(
      componentType,
      { breakpoint },
      false,
    );

    const overridePresetDefinition = preset
      ? presetDefinitions?.[preset]
      : null;

    const installation = this.getInstallationByComponentType(componentType);
    const installationPresets = installation?.presets;

    const installationDefaultBreakpointPresetKey =
      installationPresets?.default ?? installationPresets?.large;
    const installationDefaultBreakpointPresetDefinition =
      installationDefaultBreakpointPresetKey
        ? presetDefinitions?.[installationDefaultBreakpointPresetKey]
        : null;

    const installationPresetKey =
      installationPresets?.[breakpoint ?? 'default'];
    const installationPresetDefinition = installationPresetKey
      ? presetDefinitions?.[installationPresetKey]
      : null;

    return (
      overridePresetDefinition?.initialSize ??
      installationPresetDefinition?.initialSize ??
      installationDefaultBreakpointPresetDefinition?.initialSize ??
      installation?.initialSize ??
      null
    );
  }

  getLayoutByComponentType(
    componentType: string,
    options?: {
      breakpoint?: keyof NonNullable<
        NonNullable<EditorReactComponent['installation']>['presets']
      >;
      preset?: string;
    },
  ): NonNullable<EditorElement['layout']> | null {
    const manifest = this.manifestStore.getManifest(componentType);

    if (!manifest) {
      return null;
    }

    const baseLayout = manifest.editorElement?.layout ?? {};

    const { breakpoint, preset } = options ?? {};
    const presetDefinitions = this.getPresetDefinitionsByComponentType(
      componentType,
      undefined,
      false,
    );

    const overridePresetLayout = preset
      ? presetDefinitions?.[preset]?.presetDefaults?.layout
      : null;

    const installation = this.getInstallationByComponentType(componentType);
    const installationPresets = installation?.presets;

    const installationDefaultBreakpointPresetKey =
      installationPresets?.default ?? installationPresets?.large;
    const installationDefaultBreakpointPresetLayout =
      installationDefaultBreakpointPresetKey
        ? presetDefinitions?.[installationDefaultBreakpointPresetKey]
            ?.presetDefaults?.layout
        : null;

    const installationPresetKey =
      installationPresets?.[breakpoint ?? 'default'];
    const installationPresetLayout = installationPresetKey
      ? presetDefinitions?.[installationPresetKey]?.presetDefaults?.layout
      : null;

    const presetLayout =
      overridePresetLayout ??
      installationPresetLayout ??
      installationDefaultBreakpointPresetLayout;

    if (!presetLayout) {
      return baseLayout;
    }

    return { ...baseLayout, ...presetLayout };
  }
}
